import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useCollection } from "../hooks/useCollection";
import { callableErrorMessage } from "../hooks/useCallableAction";
import { LoadingBlock } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { n } from "../lib/format";
import { StatusCountCards, statusOrder, type StatusDoc } from "./Production";

/**
 * Ayak listesi — eski `leglist.php`nin karşılığı. Salt okunur, yazdırılır.
 *
 * Bir üretim statüsündeki siparişlerin reçetelerindeki "ayak tarifi" satırlarını
 * hesaplanmış adetle listeler:  adet = sipariş kalemi adedi × reçete satırı miktarı.
 *
 * DÜZELTİLEN DAVRANIŞ
 * · Eski SQL `meta_key IN ('_product_id','_variation_id')` yüzünden her kalem için
 *   İKİ satır üretiyor, tekrar ekranda ürün adı gizlenerek maskeleniyordu. Burada
 *   reçete sahibi tek anahtardır → tekrar yok.
 * · "SKU'da nokta varsa reçete ana üründen gelir" gizli kuralı artık veride açık:
 *   kalemin taşıdığı `recipeOwnerId` kullanılır, SKU string'i parçalanmaz.
 * · Zebra rengi ilk satırda tanımsız kalmıyor (eski kodda PHP notice veriyordu).
 *
 * ── DÜŞEN SATIRLAR ARTIK TABLODA (2026-08-14) ──────────────────────────────
 * "Ayak tarifi tanımsız ürünler: …" diye bir uyarı yazılıyordu ama o ürünler ve
 * BAĞLI OLDUKLARI SİPARİŞLER tablodan tamamen kayboluyordu. Atölye, uyarıdaki
 * ürün adının hangi siparişte olduğunu göremiyor, o siparişin bu statüde iş
 * beklediğini bilmiyordu. Üç düşme sebebi vardı ve üçü de sessizdi:
 *
 *   1. Reçetede ayak satırı yok      → `legs.length === 0` → yalnızca uyarı
 *   2. Kalemde reçete sahibi yok     → `!ownerId` → uyarı bile yok
 *   3. Kaynak sipariş kaydı yok      → hiç kalem okunmuyor → uyarı bile yok
 *      (canlıda aktif statülerde 10 üretim kaydı böyle)
 *
 * Üçü de artık TABLODA görünür; "Ayak Tarifi" sütununa tarif yerine gerekçe
 * yazılır ve satır işaretlenir. Adet sütununda uydurma sayı BASILMAZ.
 */

/** Security Rules'ta sipariş kalemleri `orders/{id}/items` altındadır. */
/** Sipariş kalemleri. Shopify terminolojisi; indeksler de bu adla tanımlı. */
const ITEMS_SUBCOLLECTION = "lineItems";

interface LegRow {
  key: string;
  orderId: string;
  orderLabel: string;
  productName: string;
  description: string;
  /** Ayak adedi. Tarif yoksa `null` — 0 basmak "sıfır ayak" demek olurdu. */
  quantity: number | null;
  /** Satır gerçek bir ayak tarifi değil, TANIMSIZ işaretidir. */
  undefinedRecipe: boolean;
}

export function LegList() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";

  const [rows, setRows] = useState<LegRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [missingRecipe, setMissingRecipe] = useState<string[]>([]);

  const statusesQ = useCollection<StatusDoc>(
    () => query(collection(db, "statuses"), where("category", "==", "production")),
    [],
  );
  const statuses = useMemo(
    () =>
      statusesQ.docs
        .filter((s) => s.obsolete !== true)
        .sort((a, b) => statusOrder(a) - statusOrder(b)),
    [statusesQ.docs],
  );
  const current = statuses.find((s) => s.id === status);

  const load = useCallback(async () => {
    if (!status) {
      setRows([]);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const prodSnap = await getDocs(
        query(collection(db, "production"), where("status", "==", status)),
      );
      const orderProductions = prodSnap.docs.filter((d) => d.get("sourceType") === "order");

      // Aynı reçete sahibi birden çok siparişte geçer → tek okuma, önbellek.
      const recipeCache = new Map<string, Array<{ description: string; perUnit: number }>>();
      const noRecipe = new Set<string>();
      const built: LegRow[] = [];

      for (const prod of orderProductions) {
        const orderId = String(prod.get("sourceId") ?? "");
        if (!orderId) continue;
        const orderLabel =
          String(prod.get("orderName") ?? "") ||
          String(prod.get("sourceLabel") ?? "") ||
          `#${orderId}`;

        const itemSnap = await getDocs(collection(db, "orders", orderId, ITEMS_SUBCOLLECTION));

        /* DÜŞME SEBEBİ 3: kaynak sipariş kaydı yok (göç kaybı). Eskiden bu
           üretim kaydı listede hiç görünmüyordu. */
        if (itemSnap.empty) {
          built.push({
            key: `${prod.id}::kaynak-yok`,
            orderId,
            orderLabel,
            productName: "(kalem listesi okunamadı)",
            description:
              "Kaynak sipariş veritabanında yok (göç kaybı) — ayak tarifi çıkarılamıyor. Ofise bildirin.",
            quantity: null,
            undefinedRecipe: true,
          });
          continue;
        }

        for (const item of itemSnap.docs) {
          const ownerId = String(
            item.get("recipeOwnerId") ?? item.get("productId") ?? "",
          );
          const productName = String(item.get("title") ?? item.get("productName") ?? "Ürün");
          const orderQty = Number(item.get("quantity") ?? 0);

          /* DÜŞME SEBEBİ 2: kalem hiçbir ürün kartına bağlı değil. Canlı
             örneklemde 2.000 kalemin 371'inde ne `recipeOwnerId` ne `productId`
             var; bu kalemler uyarıya bile girmeden yok oluyordu. */
          if (!ownerId) {
            built.push({
              key: `${prod.id}::${item.id}::sahipsiz`,
              orderId,
              orderLabel,
              productName,
              description:
                "Ayak tarifi TANIMSIZ — kalem bir ürün kartına bağlı değil (recipeOwnerId/productId yok).",
              quantity: null,
              undefinedRecipe: true,
            });
            noRecipe.add(productName);
            continue;
          }

          if (orderQty <= 0) {
            built.push({
              key: `${prod.id}::${item.id}::adetsiz`,
              orderId,
              orderLabel,
              productName,
              description: "Sipariş adedi 0 — ayak adedi hesaplanamıyor.",
              quantity: null,
              undefinedRecipe: true,
            });
            continue;
          }

          let legs = recipeCache.get(ownerId);
          if (!legs) {
            const legSnap = await getDocs(
              collection(db, "products", ownerId, "legs"),
            );
            /**
             * ★ SUNUCUYLA BİREBİR AYNI ÜÇ KURAL — `production/legList.ts:127-135`.
             * Bu ekran üçünü de atlıyordu, üçünün de sonucu sessizdi:
             *  1. SİLİNMİŞ ayak satırları listeye giriyordu — atölye artık
             *     üretilmeyen bir ayak için boşa iş çıkarıyor.
             *  2. Sıra rastgeleydi; sunucu `order` alanına göre sıralıyor.
             *  3. Metin yalnızca `explanation`dan okunuyordu; `description`
             *     alanını kullanan kayıtlar listede BOŞ görünüyordu.
             * İki yol aynı listeyi üretmezse hangisinin doğru olduğu belirsizleşir.
             */
            legs = legSnap.docs
              .filter((l) => l.get("deleted") !== true)
              .sort((a, b) => Number(a.get("order") ?? 0) - Number(b.get("order") ?? 0))
              .map((l) => ({
                description: String(l.get("description") || l.get("explanation") || ""),
                perUnit: Number(l.get("quantity") ?? 0),
              }));
            recipeCache.set(ownerId, legs);
          }

          /* DÜŞME SEBEBİ 1: üründe ayak reçetesi yok. */
          if (legs.length === 0) {
            noRecipe.add(productName);
            built.push({
              key: `${prod.id}::${item.id}::tarifsiz`,
              orderId,
              orderLabel,
              productName,
              description: `Ayak tarifi TANIMSIZ — ürün kartında ayak satırı yok (sipariş adedi ${orderQty}).`,
              quantity: null,
              undefinedRecipe: true,
            });
            continue;
          }

          legs.forEach((leg, i) => {
            built.push({
              key: `${prod.id}::${item.id}::${i}`,
              orderId,
              orderLabel,
              productName,
              description: leg.description || "(tarif metni girilmemiş)",
              quantity: orderQty * leg.perUnit,
              undefinedRecipe: false,
            });
          });
        }
      }

      built.sort(
        (a, b) =>
          a.orderId.localeCompare(b.orderId, "tr-TR") ||
          a.productName.localeCompare(b.productName, "tr-TR"),
      );
      setRows(built);
      setMissingRecipe([...noRecipe]);
    } catch (e) {
      console.error("Ayak listesi yüklenemedi", e);
      setLoadError(callableErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!status) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
            Ayak Listesi
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Hangi üretim statüsünün ayak listesi çıkarılacak?
          </p>
        </div>

        {statusesQ.loading ? (
          <LoadingBlock />
        ) : statuses.length === 0 ? (
          <EmptyState title="Üretim statüsü tanımlı değil" tone="warning" />
        ) : (
          /* ★ Kayıt sayısı eklendi (Üretim ekranında vardı, burada yoktu).
             `onlyOrders` — ayak listesi yalnızca siparişten doğan üretimleri
             işler; adet de aynı süzgeçle sayılır ki kart ile liste ayrışmasın. */
          <StatusCountCards
            statuses={statuses}
            hrefFor={(id) => `/leg-list?status=${encodeURIComponent(id)}`}
            onlyOrders
          />
        )}
      </div>
    );
  }

  let lastOrderId = "";
  let lastProduct = "";
  let band = 0;

  return (
    <div className="space-y-5">
      <div>
        <nav aria-label="Sayfa yolu" className="text-sm noprint">
          <ol role="list" className="flex items-center gap-2 text-gray-500">
            <li>
              <Link to="/production" className="hover:text-gray-700">
                Üretim
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-700">Ayak Listesi</li>
          </ol>
        </nav>

        <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
            Ayak Listesi — {current?.label ?? status}
          </h1>

          <div className="flex flex-wrap items-center gap-2 noprint">
            <label htmlFor="leg-status" className="sr-only">
              Statü
            </label>
            <select
              id="leg-status"
              className="select w-auto"
              value={status}
              onChange={(e) => setParams({ status: e.target.value })}
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label ?? s.id}
                </option>
              ))}
            </select>
            <button type="button" className="btn-secondary" onClick={() => window.print()}>
              Yazdır
            </button>
          </div>
        </div>
      </div>

      {loadError ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          {loadError}
        </div>
      ) : null}

      {missingRecipe.length > 0 ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 noprint">
          <span className="font-semibold">
            Ayak tarifi tanımsız {missingRecipe.length} ürün:
          </span>{" "}
          {missingRecipe.join(", ")} — bu ürünler{" "}
          <span className="font-semibold">tablodan düşürülmedi</span>, siparişleriyle birlikte
          &quot;tanımsız&quot; işaretiyle listede duruyor.
        </p>
      ) : null}

      {loading ? (
        <LoadingBlock label="Ayak listesi hazırlanıyor" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Bu statüde ayak tarifi yok"
          description="Siparişten doğan üretim kaydı ya da reçetede ayak satırı bulunamadı."
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="table-dense">
            <caption className="sr-only">
              {current?.label ?? status} statüsündeki siparişlerin ayak tarifleri
            </caption>
            <thead>
              <tr>
                <th scope="col">Sipariş</th>
                <th scope="col">Ürün</th>
                <th scope="col">Ayak Tarifi</th>
                <th scope="col">Adet</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const newOrder = r.orderId !== lastOrderId;
                if (newOrder) {
                  band += 1;
                  lastOrderId = r.orderId;
                  lastProduct = "";
                }
                const sameProduct = r.productName === lastProduct;
                lastProduct = r.productName;

                return (
                  <tr
                    key={r.key}
                    className={`${
                      // Tanımsız satır zebra rengini DEĞİL, kendi uyarı rengini alır.
                      r.undefinedRecipe
                        ? "bg-amber-50"
                        : band % 2 === 0
                          ? "bg-gray-100"
                          : "bg-white"
                    } ${newOrder ? "border-t-2 border-t-gray-800" : ""}`}
                  >
                    <td className="whitespace-nowrap">
                      {newOrder ? (
                        <Link className="link" to={`/orders/${encodeURIComponent(r.orderId)}`}>
                          {r.orderLabel}
                        </Link>
                      ) : null}
                    </td>
                    {/* Tanımsız satırda ürün adı TEKRAR BASTIRILIR: gizlenirse
                        atölye hangi ürünün tarifsiz olduğunu göremez. */}
                    <td>{sameProduct && !r.undefinedRecipe ? "" : r.productName}</td>
                    <td>
                      {r.undefinedRecipe ? (
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center rounded-md bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900">
                            tanımsız
                          </span>
                          <span>{r.description}</span>
                        </span>
                      ) : (
                        r.description
                      )}
                    </td>
                    <td className="tabular-nums font-semibold">{n(r.quantity)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default LegList;
